import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API, errorText, snapshotPayload } from './workspaceUtils';

export const useForecastWorkspace = () => {
  const [audit, setAudit] = useState(null);
  const [auditError, setAuditError] = useState('');
  const [checking, setChecking] = useState(false);
  const [snapshot, setSnapshot] = useState(null);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [listing, setListing] = useState({ snapshots: [], total: 0, offset: 0, limit: 20 });
  const serial = useRef(0);
  const refreshAudit = useCallback(async () => {
    setChecking(true); setAuditError('');
    try { setAudit((await axios.get(`${API}/forecast-workspace/readiness`, { withCredentials: true })).data); }
    catch (err) { setAudit(null); setAuditError(errorText(err)); }
    finally { setChecking(false); }
  }, []);
  const loadSnapshot = useCallback(async id => {
    const request = ++serial.current;
    setBusy('load'); setError('');
    try {
      const { data } = await axios.get(`${API}/forecast-enhanced/projection/${id}`, { withCredentials: true });
      if (request === serial.current) setSnapshot(data.projection);
    } catch (err) { if (request === serial.current) setError(errorText(err)); }
    finally { if (request === serial.current) setBusy(''); }
  }, []);
  const loadListing = useCallback(async (offset = 0) => {
    try {
      const { data } = await axios.get(`${API}/forecast-workspace/snapshots`, { params: { offset }, withCredentials: true });
      setListing(data); return data;
    } catch (err) { setError(errorText(err)); return null; }
  }, []);
  useEffect(() => {
    refreshAudit();
    loadListing().then(data => { if (data?.snapshots?.[0] && serial.current === 0) loadSnapshot(data.snapshots[0].projection_id); });
  }, [refreshAudit, loadListing, loadSnapshot]);
  const generate = async horizon => {
    ++serial.current; setBusy('generate'); setError('');
    try {
      const { data } = await axios.post(`${API}/forecast-workspace/generate`, { months_ahead: horizon }, { withCredentials: true, timeout: 120000 });
      setSnapshot(data); toast.success('Forecast generated from reviewed history');
    } catch (err) { setError(errorText(err)); if (err.response?.status === 409) refreshAudit(); }
    finally { setBusy(''); }
  };
  const save = async notes => {
    if (!snapshot) return false;
    setBusy('save'); setError('');
    try {
      const { data } = await axios.post(`${API}/forecast-enhanced/save-enhanced`, snapshotPayload(snapshot, notes), { withCredentials: true });
      await loadSnapshot(data.projection_id); await loadListing(); toast.success('Original prediction saved'); return true;
    } catch (err) { setError(errorText(err)); return false; }
    finally { setBusy(''); }
  };
  const exportFile = async comparison => {
    if (!snapshot) return;
    setBusy('export');
    try {
      const response = await axios.post(`${API}/forecast-workspace/export`, { snapshot, comparison }, { withCredentials: true, responseType: 'blob' });
      const url = URL.createObjectURL(response.data); const link = document.createElement('a'); link.href = url; link.download = `sharda-forecast-${snapshot.projection_id || snapshot.run_id}.xlsx`; link.click(); URL.revokeObjectURL(url);
    } catch (err) { setError(errorText(err)); } finally { setBusy(''); }
  };
  return { audit, auditError, checking, snapshot, busy, error, listing, refreshAudit, loadSnapshot, loadListing, generate, save, exportFile };
};